import React from "react";
import Button from "lib/common/descriptorcontrols/BaseControls/Button";
import PropTypes from "prop-types";
import { recordIdType } from "data/util/typedefs";
import EntityName from "data/util/EntityName";
import TableStore from "data/TableStore";
import { getMappingPlugins } from "data/selectors/entities";
import getMapToTypes from "lib/dataentry/lib/dataentryform/views/getMapToTypes";
import StereotypesStore from "data/StereotypesStore";
import { getActiveRecord } from "data/selectors/records";
import AdtStore from "data/AdtStore";
import HemoStore from "data/HemoStore";
import EcmoEmrStore from "data/EcmoEmrStore";
import CrossAppStore from "data/CrossAppStore";
import Dispatcher from "Dispatcher";
import ClientId from "data/ClientId";
import MapButtonActions from "data/MapButtonActions";
import useFluxStore from "@armus/use-flux-store";
import NavigatorStore from "data/NavigatorStore";

const MapButtonView = props => {
    const {
        items,
    } = props;
    if (items == null || items.length === 0) return null;
    return <Button
        label="Map"
        enabled={items.length !== 0}
        className={`btn btn-mapping`}
        options={items.map(it => ({...it, value: it.id}))}
        onClick={(e, d, item) => item.onClick && item.onClick()}
        collapse={items.length === 1 && items[0].type === "dialog"}
    />;
};

MapButtonView.propTypes = {
    items: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.any.isRequired,
        label: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        onClick: PropTypes.func, // loading items won't have one...
    })).isRequired,
};

const MapButton = props => {
    const {
        entityKey: key,
        recordId: id,
        fluxContext,
    } = props;
    const state = useFluxStore(() => {
        const activeRecord = getActiveRecord(TableStore, {key, id});
        // If there's no active record, it's because marionette hasn't caught up
        // with Flux/React yet, so we have to have this escape hatch. ActionBar
        // will be rerendering _very_ shortly, and not drawing the map button
        // since there isn't an AR, but for the moment we have to imperatively
        // code around the race. Whee!
        if (activeRecord == null) return null;

        let canUseDialog = false;
        const items = [];
        const canWrite = activeRecord.canWrite();
        if (canWrite) {
            items.push(...getMappingPlugins(activeRecord, fluxContext)
                .flatMap(p => {
                    if (p.get("id") !== "cross-record") {
                        canUseDialog = true;
                    }
                    const lo = p.searchFromActiveRecord();
                    if (lo.hasValue()) {
                        const sources = lo.getRequiredValue();
                        if (sources.length > 0) {
                            canUseDialog = true;
                        }
                        return sources.map(src => ({
                            label: p.getButtonListingPrefix() + src.getLabel(),
                            type: "map-from",
                            id: src.id,
                            onClick: () => Dispatcher.contextDispatch(fluxContext, {
                                type: MapButtonActions.AUTOMAP_FROM,
                                target: activeRecord,
                                source: src,
                            }),
                        }));
                    }
                    if (lo.isLoading()) {
                        return [{
                            ...p.loadingMessage(),
                            id: p.get("name") + "-loading",
                            type: "loading",
                        }];
                    }
                    return []; // huh? whatever.
                }));
        }
        items.push(...getMapToTypes(
            key,
            StereotypesStore.getDataEntryStereotypes(),
            targetKey => {
                const newId = ClientId.next();
                Dispatcher.contextDispatch(fluxContext, {
                    type: MapButtonActions.AUTOMAP_TO_NEW_MAGIC_CHILD,
                    entityKey: targetKey,
                    newId,
                    source: activeRecord,
                    navigate: true,
                });
            },
        ));
        if (canUseDialog) {
            items.unshift({
                label: "Launch Mapping Dialog",
                id: "dialog",
                type: "dialog",
                onClick: () =>
                    Dispatcher.contextDispatch(fluxContext, {
                        type: MapButtonActions.LAUNCH_DIALOG,
                        target: getActiveRecord(
                            TableStore,
                            NavigatorStore.getRecordState(fluxContext),
                        ),
                    })
            });
        }
        return {
            items,
        };
    }, [
        TableStore,
        AdtStore,
        HemoStore,
        EcmoEmrStore,
        CrossAppStore,
    ], [key, id, fluxContext]);
    return state == null
        ? null
        : <MapButtonView {...state} />;
};

MapButton.propTypes = {
    fluxContext: PropTypes.string.isRequired,
    recordId: recordIdType.isRequired,
    entityKey: EntityName.propType.isRequired,
};

export default MapButton;



// WEBPACK FOOTER //
// ./app/lib/mapping/mappingbutton/MapButton.js